import { Fragment, ReactNode, ReactText, Ref, useEffect, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const StyledRadioButtonLayoutContainer = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
`;

const ButtonStyleItem = styled.label<{ disabled?: boolean; checked: boolean }>`
    box-sizing: border-box;
    margin: 0;
    border: solid 0.1rem ${tokens.input.border};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    background-color: ${(props) =>
        props.checked ? tokens.text.link : tokens.background.default};
    color: ${(props) => (props.checked ? tokens.on.primary : tokens.text.primary)};

    &:hover {
        border-color: ${(props) =>
            props.disabled ? tokens.input.border : tokens.input.borderFocus};
    }

    input[type='radio'] {
        opacity: 0;
        appearance: none;
        outline: none;
    }
`;

const IndicatorStyleItem = styled.label<{ disabled?: boolean; checked: boolean }>`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    padding: 1rem;
    border: 1px solid ${tokens.input.border};
    font-weight: 500;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    background: ${(props) =>
        props.checked ? tokens.background.subtle : tokens.background.default};
    font-size: 1.4rem;

    &:focus-within {
        border-color: ${tokens.input.borderFocus};
    }

    input[type='radio'] {
        width: 15px;
        height: 15px;
        appearance: none;
        outline: none;
        border-radius: 999px;
        border: 2px solid ${tokens.input.border};
        margin-right: 0.75rem;

        &:checked {
            display: flex;
            align-items: center;
            justify-content: center;
            border-color: ${tokens.input.borderFocus};

            /* inner border / center dot */
            &:before {
                content: '';
                width: 5px;
                height: 5px;
                border-radius: 999px;
                box-sizing: border-box;
                background: ${tokens.input.borderFocus};
            }
        }
    }
`;

const tileStyles = {
    small: {
        tilePadding: '.75rem',
        inputSize: '1rem',
        inputBorderSize: '0.35rem',
        inputMargin: '.5rem',
    },
    large: {
        tilePadding: '2rem',
        inputSize: '1.6rem',
        inputBorderSize: '0.6rem',
        inputMargin: '1rem',
    },
};

const TileStyleItem = styled.label<{
    disabled?: boolean;
    checked?: boolean;
    $tileSize: RadioButtonTileSize;
}>`
    box-sizing: border-box;
    display: inline-block;
    position: relative;
    padding: ${(props) => tileStyles[props.$tileSize].tilePadding};
    padding-right: calc(
        ${(props) => tileStyles[props.$tileSize].tilePadding} +
            ${(props) => tileStyles[props.$tileSize].inputSize}
    );
    border: 1px solid
        ${(props) => (props.checked ? tokens.input.borderFocus : tokens.input.border)};
    font-weight: 500;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

    &:focus-within,
    &:hover {
        border-color: ${tokens.input.borderFocus};
    }

    input[type='radio'] {
        box-sizing: border-box;
        position: absolute;
        top: 0;
        right: 0;
        width: ${(props) => tileStyles[props.$tileSize].inputSize};
        height: ${(props) => tileStyles[props.$tileSize].inputSize};
        margin: ${(props) => tileStyles[props.$tileSize].inputMargin};
        opacity: 0;
        appearance: none;
        outline: none;

        &:checked {
            opacity: 1;
            border-radius: ${(props) => tileStyles[props.$tileSize].inputSize};
            border: ${(props) => tileStyles[props.$tileSize].inputBorderSize} solid
                ${tokens.input.borderFocus};
        }
    }
`;

const TileRadioButtonLayoutContainer = styled(RadioButtonLayoutContainer)`
    gap: 1rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
`;

const SelectedContentContainer = styled.div`
    padding: 1rem 2rem;
    border: 1px solid ${tokens.input.border};
    font-size: 1.4rem;
`;

const getColumnCount = (
    itemCount: number,
    layout?: RadioButtonGroupLayout,
    columns?: number,
) => ((layout ?? 'vertical') === 'horizontal' ? Math.max(1, columns ?? itemCount) : 1);

type RadioButtonLayoutContainerProps = Readonly<{
    columns: number;
    children: ReactNode;
    className?: string;
    ariaLabelledBy?: string;
    ref?: Ref<HTMLDivElement>;
}>;

function RadioButtonLayoutContainer({
    className,
    ref,
    children,
    columns,
    ariaLabelledBy,
}: RadioButtonLayoutContainerProps) {
    return (
        <StyledRadioButtonLayoutContainer
            $columns={columns}
            ref={ref}
            role="radiogroup"
            aria-labelledby={ariaLabelledBy}
            className={`inline-block text-nowrap ${className}`}
        >
            {children}
        </StyledRadioButtonLayoutContainer>
    );
}

type ItemRenderer<T extends RadioButtonValue> = (
    item: RadioButtonItem<T>,
    checked: boolean,
    radioInput: ReactNode,
) => ReactNode;

type ItemsRenderer<T extends RadioButtonValue> = Readonly<{
    renderItems: (renderItem: ItemRenderer<T>) => ReactNode;
}>;

type ButtonStyleRadioButtonGroupItemsProps<T extends RadioButtonValue> =
    RadioButtonGroupProps<T> & ButtonRadioButtonGroupProps & ItemsRenderer<T>;

function ButtonStyleRadioButtonGroupItems<T extends RadioButtonValue>({
    ref,
    buttonClass,
    className,
    renderItems,
    ariaLabelledBy,
    disabled,
}: ButtonStyleRadioButtonGroupItemsProps<T>) {
    return (
        <div
            ref={ref}
            role="radiogroup"
            aria-labelledby={ariaLabelledBy}
            className={`inline-block text-nowrap ${className ?? ''}`}
        >
            {renderItems((item, checked, radioInput) => (
                <ButtonStyleItem
                    role="radio"
                    disabled={item.disabled ?? disabled}
                    checked={checked}
                    aria-checked={checked}
                    className={`inline-block px-10 py-3 ${buttonClass ?? ''}`}
                    title={item.title}
                    aria-label={item.ariaLabel ?? item.title}
                >
                    {item.label ?? item.value}
                    {radioInput}
                </ButtonStyleItem>
            ))}
        </div>
    );
}

type IndicatorStyleGroupItemsProps<T extends RadioButtonValue> =
    RadioButtonGroupProps<T> & IndicatorRadioButtonGroupProps & ItemsRenderer<T>;

function IndicatorStyleGroupItems<T extends RadioButtonValue>({
    ref,
    buttonClass,
    className,
    renderItems,
    ariaLabelledBy,
    disabled,
    layout,
    columns,
    items,
}: IndicatorStyleGroupItemsProps<T>) {
    return (
        <RadioButtonLayoutContainer
            ref={ref}
            columns={getColumnCount(items.length, layout, columns)}
            className={className}
            aria-labelledby={ariaLabelledBy}
        >
            {renderItems((item, checked, radioInput) => (
                <div>
                    <IndicatorStyleItem
                        role="radio"
                        disabled={item.disabled ?? disabled}
                        checked={checked}
                        aria-checked={checked}
                        title={item.title}
                        aria-label={item.ariaLabel ?? item.title}
                        className={buttonClass}
                    >
                        {radioInput}
                        {item.label ?? item.value}
                    </IndicatorStyleItem>
                </div>
            ))}
        </RadioButtonLayoutContainer>
    );
}

type ExpandableStyleGroupItemsProps<T extends RadioButtonValue> =
    RadioButtonGroupProps<T> & ExpandableRadioButtonGroupProps & ItemsRenderer<T>;

function ExpandableStyleGroupItems<T extends RadioButtonValue>({
    ref,
    buttonClass,
    className,
    renderItems,
    ariaLabelledBy,
    disabled,
}: ExpandableStyleGroupItemsProps<T>) {
    return (
        <RadioButtonLayoutContainer
            ref={ref}
            columns={1}
            className={className}
            aria-labelledby={ariaLabelledBy}
        >
            {renderItems((item, checked, radioInput) => (
                <div>
                    <IndicatorStyleItem
                        role="radio"
                        disabled={disabled}
                        checked={checked}
                        aria-checked={checked}
                        title={item.title}
                        aria-label={item.ariaLabel ?? item.title}
                        className={buttonClass}
                    >
                        {radioInput}
                        {item.label ?? item.value}
                    </IndicatorStyleItem>
                    {checked && item.selectedContent && (
                        <SelectedContentContainer>
                            {item.selectedContent}
                        </SelectedContentContainer>
                    )}
                </div>
            ))}
        </RadioButtonLayoutContainer>
    );
}

type TileStyleGroupItemsProps<T extends RadioButtonValue> = RadioButtonGroupProps<T> &
    TileRadioButtonGroupProps &
    ItemsRenderer<T>;

function TileStyleGroupItems<T extends RadioButtonValue>({
    tileSize,
    ref,
    buttonClass,
    className,
    renderItems,
    ariaLabelledBy,
    disabled,
    layout,
    columns,
    items,
}: TileStyleGroupItemsProps<T>) {
    return (
        <TileRadioButtonLayoutContainer
            ref={ref}
            columns={getColumnCount(items.length, layout, columns)}
            className={className}
            aria-labelledby={ariaLabelledBy}
        >
            {renderItems((item, checked, radioInput) => (
                <TileStyleItem
                    role="radio"
                    disabled={disabled}
                    checked={checked}
                    aria-checked={checked}
                    $tileSize={tileSize ?? 'large'}
                    title={item.title}
                    aria-label={item.ariaLabel ?? item.title}
                    className={buttonClass}
                >
                    {item.label ?? item.value}
                    {radioInput}
                </TileStyleItem>
            ))}
        </TileRadioButtonLayoutContainer>
    );
}

export type RadioButtonValue = ReactText | undefined;

export type RadioButtonItem<T extends RadioButtonValue> = Readonly<{
    /**
     * Value of radio button item.
     */
    value: T;

    /**
     * The label of the radio button item.  If no label is supplied, the value will be used.
     */
    label?: ReactNode;

    /**
     * Additional content to show when the radio button is selected for expandable types.
     */
    selectedContent?: ReactNode;

    /**
     * HTML title attribute content, shown when moused over.
     */
    title?: string;

    /**
     * The label to show for accessibility, defaults to title otherwise.
     */
    ariaLabel?: string;

    /**
     * Key of the options. If not supplied will use value
     */
    key?: string;

    /**
     * Is the input currently disabled
     */
    disabled?: boolean;
}>;

export type RadioButtonGroupLayout = 'horizontal' | 'vertical';

export type RadioButtonGroupLayoutConfig = Readonly<{
    /**
     * The direction of the radio button items (defaults to `vertical`).
     */
    layout?: RadioButtonGroupLayout;

    /**
     * The number of columns when `layout` is `horizontal` (defaults to the number of items).
     */
    columns?: number;
}>;

export type IndicatorRadioButtonGroupProps = Readonly<{
    radioStyle: 'indicator';
}> &
    RadioButtonGroupLayoutConfig;

export type RadioButtonTileSize = 'small' | 'large';

export type TileRadioButtonGroupProps = Readonly<{
    radioStyle: 'tile';

    /**
     * The tile size (defaults to `large`).
     */
    tileSize?: RadioButtonTileSize;
}> &
    RadioButtonGroupLayoutConfig;

export type ButtonRadioButtonGroupProps = Readonly<{
    radioStyle: 'button';
}>;

export type ExpandableRadioButtonGroupProps = Readonly<{
    radioStyle: 'expandable';
}>;

export type RadioButtonGroupProps<T extends RadioButtonValue> = Readonly<
    {
        /**
         * The style of radio button.
         *
         * * `button` - Radio buttons styled as sticky buttons.
         * * `indicator` - Radio button styled with radio input to the left.
         * * `tile` - Radio buttons styled as tiles with radio input in upper right.
         * * `expandable` - Vertical radio buttons that can show additional content when selected.
         */
        radioStyle: 'button' | 'indicator' | 'tile' | 'expandable';

        /**
         * The radio button items.
         */
        items: RadioButtonItem<T>[];

        /**
         * The name of the radio button group (applied to the radio button inputs).
         */
        name: string;

        /**
         * The current value.
         */
        value?: T;

        /**
         * Called when the current value changes.
         */
        onChange?: (value: T) => void;

        /**
         * On blur event.
         */
        onBlur?: () => void;

        /**
         * Class to apply to the buttons.
         */
        buttonClass?: string;

        /**
         * Class to apply to the radio button group container.
         */
        className?: string;

        /**
         * Set container `aria-labelledby` attribute.
         */
        ariaLabelledBy?: string;

        /**
         * Ref to radio button group container.
         */
        ref?: Ref<HTMLDivElement>;

        /**
         * Indicates if the radio button group is disabled (defaults to `false`).
         */
        disabled?: boolean;
    } & (
        | ButtonRadioButtonGroupProps
        | IndicatorRadioButtonGroupProps
        | TileRadioButtonGroupProps
        | ExpandableRadioButtonGroupProps
    )
>;

export function RadioButtonGroup<T extends RadioButtonValue>(
    props: RadioButtonGroupProps<T>,
) {
    const { items, name, value, onChange, onBlur } = props;

    const [selected, setSelected] = useState<T | undefined>(value);

    useEffect(() => {
        setSelected(value);
    }, [value]);

    const selectItem = (itemValue: T) => {
        setSelected(itemValue);
        if (onChange && itemValue !== value) {
            onChange(itemValue);
        }
    };

    const renderItems = (renderer: ItemRenderer<T>) =>
        items.map((item) => (
            <Fragment key={item.key ?? item.value}>
                {renderer(
                    item,
                    item.value === selected,
                    <input
                        type="radio"
                        checked={item.value === selected}
                        name={name}
                        value={item.value}
                        onChange={() => {
                            selectItem(item.value);
                        }}
                        onBlur={onBlur}
                        disabled={item.disabled}
                    />,
                )}
            </Fragment>
        ));

    switch (props.radioStyle) {
        case 'button':
            return (
                <ButtonStyleRadioButtonGroupItems {...props} renderItems={renderItems} />
            );

        case 'indicator':
            return <IndicatorStyleGroupItems {...props} renderItems={renderItems} />;

        case 'tile':
            return <TileStyleGroupItems {...props} renderItems={renderItems} />;

        case 'expandable':
            return <ExpandableStyleGroupItems {...props} renderItems={renderItems} />;

        default:
            throw new Error('Invalid radioStyle');
    }
}
